/**
 * InviteConsumer — auto-consume `?invite=<token>` for already-signed-in users.
 *
 * T16 Stage 4 Bug 2 fix.
 *
 * The redirect chain previously dropped the invite token whenever a signed-in
 * user clicked an email link like `https://dev.shopista.ca/?invite=<token>`:
 *
 *   `/?invite=<token>`  → App.tsx  → `<Navigate to=/home replace />`
 *
 * The Navigate hop strips the query string and `/home` (ShoppingListPage) has no
 * reader for `?invite=`, so the token was discarded silently. Even worse:
 * the user got no error feedback because no code path knew the invite ever
 * existed.
 *
 * This component sits inside the protected tree just under `<AuthGuard>` and
 * intercepts the URL on every navigation. When it spots `?invite=<token>` on
 * a signed-in session it:
 *
 *   1. Suspends its children so the inner `<Routes>`' default `<Navigate>`
 *      hops (e.g., `/` -> `/home`) cannot fire and tear the page out from
 *      under the in-flight join request. A page-tear during the fetch shows
 *      up as `TypeError: Failed to fetch` even though the backend completed
 *      the join — the response just never reaches the discarded document.
 *   2. Calls `api.households.join({ inviteToken })` once (ref-guarded so
 *      React 18 StrictMode double-invocation can't replay a one-shot token).
 *   3. On success: applies the rotated session token (the join response
 *      carries `{ user, sessionToken }` — same shape SettingsPage's
 *      JoinHouseholdCard already consumes), wipes the query cache so reads
 *      refetch under the new household scope, strips `?invite=` from the URL
 *      and navigates to `/home`. Then unmounts the suspension overlay so
 *      children render under the new session.
 *   4. On failure: surfaces `apiErrorMessage(err)` via an inline banner above
 *      the page content. The user is still signed in — failure here is
 *      "the invite did not apply", not "you cannot view the app".
 *
 * It also reads `?invite_error=<msg>` (set by `OAuthCallback` when sign-in
 * succeeded but the invite was rejected by the backend's `inviteError` field
 * — e.g. email mismatch, expired, hard-deleted) and renders it in the same
 * banner. T16 Stage 4 AC 4.3/4.4.
 *
 * Auto-consume only runs when the user is signed in (a token is present in
 * storage). When unauthenticated the AuthGuard above us has already redirected
 * to /login with the token preserved on the URL — that path is the LoginPage's
 * job, not ours.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useLocation,
  useNavigate,
  type Location,
} from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { apiErrorMessage } from '@shopista/shared/api';
import { useJoinHousehold } from '@shopista/shared/api/hooks';
import { routes } from '../routes/paths';
import { useTokens } from './useTokens';

const bannerStyle = {
  padding: '0.75rem 1rem',
  marginBottom: '1rem',
  borderRadius: '0.375rem',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  fontSize: '0.875rem',
} as const;

const errorBannerStyle = {
  ...bannerStyle,
  borderColor: 'var(--color-status-danger)',
  backgroundColor: 'var(--color-bg-muted)',
  color: 'var(--color-status-danger)',
};

const pendingStyle = {
  padding: '2rem',
  textAlign: 'center',
  color: 'var(--color-text-muted)',
} as const;

export interface InviteConsumerProps {
  children: React.ReactNode;
}

function readInviteFromLocation(location: Location): {
  inviteToken: string | null;
  inviteError: string | null;
} {
  const params = new URLSearchParams(location.search);
  return {
    inviteToken: params.get('invite'),
    inviteError: params.get('invite_error'),
  };
}

function stripInviteParamsFromUrl(): void {
  // Use `history.replaceState` so we don't push a navigation entry for the
  // cleanup. We strip both `invite` and `invite_error` so a refresh after the
  // banner is dismissed doesn't replay the same flow.
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('invite');
    url.searchParams.delete('invite_error');
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, '', next);
  } catch {
    // history may be sandboxed in some test environments; the navigate()
    // call below is the reliable cleanup. This is a best-effort polish.
  }
}

export function InviteConsumer({ children }: InviteConsumerProps): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const tokens = useTokens();
  const queryClient = useQueryClient();
  const join = useJoinHousehold();

  // Ref-guard: the join endpoint consumes the invite token (single-use). If
  // React 18 StrictMode mounts the component twice or the user does a
  // back/forward navigation that re-renders us, we MUST NOT fire the join
  // a second time. Mirrors the `startedRef` guard in OAuthCallback.tsx.
  const consumedTokenRef = useRef<string | null>(null);

  const [inlineError, setInlineError] = useState<string | null>(null);

  // While `consuming` is true, we suppress rendering of `children`. This
  // matters because the inner `<Routes>` in App.tsx maps `/` to
  // `<Navigate to="/home" replace />`. If those Routes render concurrently
  // with our in-flight join, the route-change tears the page out from under
  // the fetch and the join response never lands on this document.
  const [consuming, setConsuming] = useState<boolean>(() => {
    // Synchronous initial check so the first render already gates children
    // before the inner Routes get a chance to fire their Navigate effect.
    if (typeof window === 'undefined') return false;
    const initial = new URLSearchParams(window.location.search).get('invite');
    return Boolean(initial);
  });

  // Read both fields up-front so deps stay stable per render.
  const { inviteToken, inviteError } = useMemo(
    () => readInviteFromLocation(location),
    [location],
  );

  // Surface backend-provided inviteError immediately. This handles the case
  // where OAuthCallback redirected here as `/home?invite_error=...` because
  // sign-in succeeded but the invite token was rejected (mismatched email,
  // expired, already-used).
  useEffect(() => {
    if (inviteError) {
      setInlineError(inviteError);
      // Clean the URL so a refresh doesn't replay the banner.
      stripInviteParamsFromUrl();
    }
  }, [inviteError]);

  useEffect(() => {
    if (!inviteToken) {
      // No token in URL — make sure we're not stuck in the consuming gate
      // (e.g., navigation back to a clean URL after consumption finished).
      setConsuming(false);
      return;
    }
    if (consumedTokenRef.current === inviteToken) return;

    // A token only auto-consumes when the user is signed in. If there's no
    // access token, AuthGuard above already redirected — we should never
    // reach this component without one. Guard defensively anyway.
    if (tokens.getAccessToken() === null) {
      setConsuming(false);
      return;
    }

    consumedTokenRef.current = inviteToken;
    setConsuming(true);

    void (async () => {
      try {
        const resp = await join.mutateAsync({ inviteToken });
        // Apply the rotated session (same dance JoinHouseholdCard does).
        if (resp.sessionToken && resp.user) {
          tokens.setSession({
            accessToken: resp.sessionToken,
            user: resp.user,
          });
        }
        // The new session is scoped to the inviting household — wipe every
        // cached read so refetches happen under the new scope.
        queryClient.clear();
        // Strip the `?invite=` param and land on /home.
        stripInviteParamsFromUrl();
        navigate(routes.home, { replace: true });
      } catch (err) {
        setInlineError(apiErrorMessage(err, 'Could not apply invite.'));
        // Clean the URL even on failure so the user can navigate away
        // without re-triggering the same failed attempt on every render.
        stripInviteParamsFromUrl();
      } finally {
        // Release the suspension overlay so children render under the new
        // (or unchanged, on failure) session.
        setConsuming(false);
      }
    })();
    // We intentionally exclude `join`, `tokens`, `queryClient`, `navigate`
    // from deps — they are stable across renders and including them would
    // re-fire the effect on every parent render. The ref-guard plus
    // inviteToken dep is what controls run-exactly-once-per-token.
  }, [inviteToken]);

  return (
    <>
      {inlineError && (
        <div
          style={errorBannerStyle}
          role="alert"
          data-testid="invite-consumer-error"
        >
          {inlineError}
        </div>
      )}
      {consuming ? (
        <div data-testid="invite-consumer-pending" style={pendingStyle}>
          Applying invite...
        </div>
      ) : (
        children
      )}
    </>
  );
}
